/* JapanSolved™ Philippines Chamber v53 VISUAL TUNING
   Purpose:
   - Stable dark/light theme sync.
   - Stable hero slideshow.
   - Safe reveal system that never leaves the page blank if JS stalls.
   - One-image card thumbnail swapping for desktop/mobile + dark/light.
*/
(function(){
  if(window.__JSPH_CHAMBER_V53__) return;
  window.__JSPH_CHAMBER_V53__ = true;

  const ROOT_SELECTOR = '[data-jsph-chamber-v3], .jsph-v3';
  const roots = () => Array.from(document.querySelectorAll(ROOT_SELECTOR));
  const mqMobile = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
  const mqLight = window.matchMedia ? window.matchMedia('(prefers-color-scheme: light)') : null;
  let rafId = null;
  let revealInstalled = false;
  let heroInstalled = false;
  let floatingInstalled = false;
  let progressInstalled = false;

  function normalize(text){
    return String(text || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function textTheme(text){
    const t = normalize(text);
    if(!t) return null;
    if(t.includes('light') || t.includes('ライト') || t.includes('明る')) return 'light';
    if(t.includes('dark') || t.includes('ダーク') || t.includes('暗')) return 'dark';
    return null;
  }

  function tokenTheme(text){
    const t = normalize(text);
    if(!t) return null;
    if(/(^|\s|_|-)(light|theme-light|scheme-light|color-scheme-light)(\s|_|-|$)/.test(t)) return 'light';
    if(/(^|\s|_|-)(dark|theme-dark|scheme-dark|color-scheme-dark)(\s|_|-|$)/.test(t)) return 'dark';
    return null;
  }

  function readStoredTheme(){
    const keys = ['theme','color-theme','color_scheme','color-scheme','shopify-color-scheme','preferred-theme','prefers-color-scheme'];
    try {
      for(const key of keys){
        const lv = window.localStorage ? window.localStorage.getItem(key) : null;
        const sv = window.sessionStorage ? window.sessionStorage.getItem(key) : null;
        const a = tokenTheme(lv); if(a) return a;
        const b = tokenTheme(sv); if(b) return b;
      }
    } catch(err){}
    return null;
  }

  function readDomTheme(){
    const html = document.documentElement;
    const body = document.body;
    const tokens = [
      html.className,
      body ? body.className : '',
      html.getAttribute('data-theme'),
      body ? body.getAttribute('data-theme') : '',
      html.getAttribute('data-color-scheme'),
      body ? body.getAttribute('data-color-scheme') : '',
      html.getAttribute('color-scheme'),
      body ? body.getAttribute('color-scheme') : ''
    ];
    for(const token of tokens){
      const res = tokenTheme(token);
      if(res) return res;
    }
    return null;
  }

  function readHeaderToggleTheme(){
    const selectors = [
      '[aria-label*="theme" i]',
      '[aria-label*="dark" i]',
      '[aria-label*="light" i]',
      '[data-theme-toggle]',
      '[data-color-scheme-toggle]',
      '.theme-toggle',
      '.color-scheme-toggle',
      'button',
      'a'
    ];
    const candidates = Array.from(document.querySelectorAll(selectors.join(',')));
    for(const el of candidates){
      const rect = el.getBoundingClientRect ? el.getBoundingClientRect() : { width:0, height:0 };
      if(rect.width < 28 || rect.height < 16) continue;
      const combined = [
        el.getAttribute('aria-label'),
        el.getAttribute('title'),
        el.getAttribute('data-theme'),
        el.getAttribute('data-current-theme'),
        el.getAttribute('data-color-scheme'),
        el.textContent
      ].filter(Boolean).join(' ');
      const visibleState = textTheme(combined) || tokenTheme(combined);
      if(!visibleState) continue;
      /* JapanSolved header shows the target mode, not the active mode. */
      return visibleState === 'dark' ? 'light' : 'dark';
    }
    return null;
  }

  function resolveTheme(root){
    const forced = root && root.getAttribute('data-jsph-theme-mode');
    if(forced === 'light') return 'light';
    if(forced === 'dark') return 'dark';
    return readDomTheme() || readStoredTheme() || readHeaderToggleTheme() || (mqLight && mqLight.matches ? 'light' : 'dark');
  }

  function resolveDevice(){
    return mqMobile && mqMobile.matches ? 'mobile' : 'desktop';
  }

  function detectLanguage(root){
    const explicit = (root && root.getAttribute('data-jsph-language')) || document.documentElement.lang || (document.body && document.body.getAttribute('lang')) || '';
    const haystack = [explicit, location.pathname, document.title, root ? (root.textContent || '').slice(0, 2000) : ''].join(' ');
    if(/[ぁ-んァ-ヶ一-龠々]{8,}|日本語|フィリピン|ご相談|重要事項|アウトバウンド/.test(haystack)) return 'ja';
    if(/philippines|outbound|advisory|request|private/i.test(haystack)) return 'en';
    return 'unknown';
  }

  function applyRootState(root, theme, device){
    const lang = detectLanguage(root);
    root.classList.add('jsph-js-ready');
    root.classList.remove('jsph-theme-light','jsph-theme-dark','jsph-theme-auto','jsph-is-light','jsph-is-dark','jsph-lang-ja','jsph-lang-en','jsph-lang-unknown');
    root.classList.add(theme === 'light' ? 'jsph-theme-light' : 'jsph-theme-dark');
    root.classList.add(theme === 'light' ? 'jsph-is-light' : 'jsph-is-dark');
    root.classList.add('jsph-lang-' + lang);
    root.setAttribute('data-jsph-resolved-theme', theme);
    root.setAttribute('data-jsph-device', device);
    root.setAttribute('data-jsph-lang', lang);

    root.querySelectorAll('.jsph-main').forEach((main) => {
      main.setAttribute('data-js-view-mode', theme);
      main.setAttribute('data-js-language', lang === 'unknown' ? 'ja' : lang);
    });
  }

  function syncCardImages(root, theme, device){
    const dataKey = device + theme.charAt(0).toUpperCase() + theme.slice(1); // desktopDark
    const attrKey = 'data-' + device + '-' + theme;
    root.querySelectorAll('.jsph-card-img-active').forEach((img) => {
      const next = img.dataset[dataKey] || img.getAttribute(attrKey) || img.dataset.desktopDark || img.getAttribute('data-desktop-dark') || img.getAttribute('src');
      if(next && img.getAttribute('src') !== next){
        img.setAttribute('src', next);
      }
      const thumb = img.closest('.jsph-card-thumb');
      if(thumb){
        thumb.classList.add('jsph-thumb-loaded');
        thumb.setAttribute('data-jsph-current-lane', device + '-' + theme);
      }
    });
  }

  function syncNow(){
    const device = resolveDevice();
    roots().forEach((root) => {
      const theme = resolveTheme(root);
      applyRootState(root, theme, device);
      syncCardImages(root, theme, device);
    });
  }

  function scheduleSync(){
    if(rafId) return;
    rafId = window.requestAnimationFrame(() => {
      rafId = null;
      syncNow();
    });
  }

  function delayedSync(){
    scheduleSync();
    window.setTimeout(scheduleSync, 60);
    window.setTimeout(scheduleSync, 180);
    window.setTimeout(scheduleSync, 420);
    window.setTimeout(scheduleSync, 900);
  }

  function installReveal(){
    if(revealInstalled) return;
    revealInstalled = true;
    const nodes = Array.from(document.querySelectorAll(
      ROOT_SELECTOR + ' [data-jsph-reveal], ' +
      ROOT_SELECTOR + ' .phm-reveal'
    ));

    if(!nodes.length) return;

    const showAll = () => nodes.forEach((node) => node.classList.add('is-visible'));

    if('IntersectionObserver' in window){
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if(entry.isIntersecting){
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      }, { threshold:0.10, rootMargin:'0px 0px -4% 0px' });

      nodes.forEach((node, i) => {
        node.style.transitionDelay = Math.min(i * 38, 260) + 'ms';
        io.observe(node);
      });

      window.setTimeout(showAll, 1800);
    } else {
      showAll();
    }
  }

  function installHeroRotation(){
    if(heroInstalled) return;
    heroInstalled = true;
    roots().forEach((root) => {
      const slider = root.querySelector('[data-jsph-slider]') || root;
      const mediaSlides = Array.from(root.querySelectorAll('[data-jsph-media-slide]'));
      const buttons = Array.from(root.querySelectorAll('[data-jsph-jump]'));
      const meter = root.querySelector('[data-jsph-meter]');
      if(mediaSlides.length <= 1) return;
      let active = 0;
      const autoplay = root.getAttribute('data-autoplay') === 'true';
      const raw = Number(root.getAttribute('data-autoplay-speed'));
      const speed = Number.isFinite(raw) ? Math.min(9000, Math.max(5800, raw)) : 7000;
      const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const setActive = (index) => {
        active = (index + mediaSlides.length) % mediaSlides.length;
        mediaSlides.forEach((slide, i) => slide.classList.toggle('is-active', i === active));
        buttons.forEach((button, i) => {
          button.classList.toggle('is-active', i === active);
          button.setAttribute('aria-selected', i === active ? 'true' : 'false');
        });
        if(slider) slider.style.setProperty('--jsph-active-slide', String(active));
        if(meter) meter.style.transform = 'translateX(' + (active * 100) + '%)';
      };

      setActive(0);
      buttons.forEach((button, i) => {
        button.addEventListener('click', () => {
          setActive(i);
          if(root.__jsphHeroTimer){
            clearInterval(root.__jsphHeroTimer);
            root.__jsphHeroTimer = null;
          }
          if(autoplay && !reduced){
            root.__jsphHeroTimer = window.setInterval(() => setActive(active + 1), speed);
          }
        });
      });

      if(autoplay && !reduced && !root.__jsphHeroTimer){
        root.__jsphHeroTimer = window.setInterval(() => setActive(active + 1), speed);
      }
    });
  }

  function installProgress(){
    if(progressInstalled) return;
    progressInstalled = true;
    const progress = document.querySelector('[data-jsph-progress]');
    if(!progress) return;
    const setProgress = () => {
      const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const ratio = Math.min(1, Math.max(0, window.scrollY / max));
      progress.style.transform = 'scaleX(' + ratio + ')';
    };
    setProgress();
    window.addEventListener('scroll', setProgress, { passive:true });
    window.addEventListener('resize', setProgress, { passive:true });
  }

  function installFloatingCTA(){
    if(floatingInstalled) return;
    floatingInstalled = true;
    const button = document.querySelector('.phm-floating-button');
    if(!button) return;
    const toggle = () => button.classList.toggle('is-visible', window.scrollY > 680);
    toggle();
    window.addEventListener('scroll', toggle, { passive:true });
  }

  function boot(){
    installReveal();
    installHeroRotation();
    installProgress();
    installFloatingCTA();
    delayedSync();
  }

  if(document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', boot, { once:true });
  } else {
    boot();
  }
  window.addEventListener('load', boot, { once:true });
  window.addEventListener('pageshow', delayedSync);
  window.addEventListener('resize', delayedSync, { passive:true });
  window.addEventListener('orientationchange', delayedSync, { passive:true });
  window.addEventListener('storage', delayedSync);

  document.addEventListener('click', function(event){
    const target = event.target;
    if(!target || !target.closest) return;
    const clickedToggle = target.closest('[data-theme-toggle], [data-color-scheme-toggle], .theme-toggle, .color-scheme-toggle, button, a');
    if(!clickedToggle) return;
    const signature = [
      clickedToggle.getAttribute('aria-label'),
      clickedToggle.getAttribute('title'),
      clickedToggle.getAttribute('class'),
      clickedToggle.getAttribute('data-theme'),
      clickedToggle.getAttribute('data-color-scheme'),
      clickedToggle.textContent
    ].filter(Boolean).join(' ').toLowerCase();
    if(signature.includes('theme') || signature.includes('dark') || signature.includes('light') || signature.includes('ダーク') || signature.includes('ライト')){
      delayedSync();
    }
  }, true);

  if(window.matchMedia){
    [mqMobile, mqLight].forEach((mq) => {
      if(!mq) return;
      if(typeof mq.addEventListener === 'function') mq.addEventListener('change', delayedSync);
      else if(typeof mq.addListener === 'function') mq.addListener(delayedSync);
    });
  }

  if('MutationObserver' in window){
    [document.documentElement, document.body].filter(Boolean).forEach((node) => {
      const observer = new MutationObserver(delayedSync);
      observer.observe(node, {
        attributes:true,
        attributeFilter:['class','style','data-theme','data-color-scheme','color-scheme','lang']
      });
    });
  }
})();
